import React, { createContext, useReducer } from "react";
import PropTypes from "prop-types";

const initialState = {
  mode: null,
  map: null,
  draw: null,
  farm: null,
  fields: [],
  selectedFields: [],
  drawerOpen: false,
  measureMode: "",
  exportMode: false,
  inspect: false,
};

const viewState = {
  tutorialMode: false,
  gettingStarted: false,
  selectedLayer: "standard",
  algorithms: [],
  selectedAlgorithm: "NDVI",
  catalog: [],
  selectedDate: null,
  selectedZone: null,
  selectedStack: null,
  rasterLoading: false,
  rasterTiles: 0,
  harvestMoistureData: null,
};

const rapidEmState = {
  tutorialMode: false,
  gettingStarted: false,
};

const manageState = {
  cropTypes: [],
  currentField: null,
  manageStatus: null,
};

const zoningState = {
  zoneLayerTransparency: 1,
  zoneLayerFetching: false,
  zoneData: [],
};

const MapActions = {
  SET_DATA: "SET_DATA",
  SET_MAP: "SET_MAP",
  SET_DRAW: "SET_DRAW",
  SET_TUTORIAL_MODE: "SET_TUTORIAL_MODE",
  SET_VIEW_MODE: "SET_VIEW_MODE",
  SET_MANAGE_MODE: "SET_MANAGE_MODE",
  SET_ZONING_MODE: "SET_ZONING_MODE",
  SET_ZONING_EDIT_MODE: "SET_ZONING_EDIT_MODE",
  SET_SELECTED_FIELDS: "SET_SELECTED_FIELDS",
  REMOVE_SELECTED_FIELDS: "REMOVE_SELECTED_FIELDS",
  SET_SELECTED_LAYER: "SET_SELECTED_LAYER",
  SET_CATALOG: "SET_CATALOG",
  FETCH_MORE_CATALOG: "FETCH_MORE_CATALOG",
  SET_SELECTED_DATE: "SET_SELECTED_DATE",
  SET_RASTER_LOADING: "SET_RASTER_LOADING",
  SET_RASTER_TILES: "SET_RASTER_TILES",
  SET_ALGORITHMS: "SET_ALGORITHMS",
  SET_SELECTED_ALGORITHM: "SET_SELECTED_ALGORITHM",
  SET_NAVIGATE_FARM: "SET_NAVIGATE_FARM",
  SET_CREATE_BOUNDARY: "SET_CREATE_BOUNDARY",
  SET_CURRENT_FIELD: "SET_CURRENT_FIELD",
  SET_CREATE_NEW_FIELD: "SET_CREATE_NEW_FIELD",
  SET_MANAGE_STATUS: "SET_MANAGE_STATUS",
  SET_CROP_TYPES: "SET_CROP_TYPES",
  CANCEL_CREATE_FIELD: "CANCEL_CREATE_FIELD",
  SAVE_NEW_FIELD: "SAVE_NEW_FIELD",
  EDIT_SELECTED_FIELD: "EDIT_SELECTED_FIELD",
  SAVE_EDIT_FIELD: "SAVE_EDIT_FIELD",
  CANCEL_EDIT_FIELD: "CANCEL_EDIT_FIELD",
  REMOVE_FIELD: "REMOVE_FIELD",
  SWITCH_FIELD: "SWITCH_FIELD",
  SET_MEASURE_MODE: "SET_MEASURE_MODE",
  SET_BOOK_RAPID_EM: "SET_BOOK_RAPID_EM",
  SET_EXPORT_MODE: "SET_EXPORT_MODE",
  SET_START_ZONING: "SET_START_ZONING",
  SET_ZONE_LAYER_TRANSPARENCY: "SET_ZONE_LAYER_TRANSPARENCY",
  SET_ZONE_LAYER_FETCHING: "SET_ZONE_LAYER_FETCHING",
  SET_ZONE_DATA: "SET_ZONE_DATA",
  SET_SELECTED_ZONE: "SET_SELECTED_ZONE",
  SET_TOGGLE_CALCULATIONS: "SET_TOGGLE_CALCULATIONS",
  SET_STACK_DATA: "SET_STACK_DATA",
  SET_INSPECT: "SET_INSPECT",
  SET_HARVEST_MOISTURE_DATA: "SET_HARVEST_MOISTURE_DATA",
};

const reducer = (state, action) => {
  switch (action.type) {
    case MapActions.SET_DATA: {
      const { farm, fields } = action.value;

      return { ...initialState, farm, fields };
    }
    case MapActions.SET_MAP:
      return { ...state, map: action.value };
    case MapActions.SET_DRAW:
      return { ...state, draw: action.value };
    case MapActions.SET_VIEW_MODE: {
      const { map, draw, farm, fields, selectedFields, rasterTiles } = state;

      return {
        map,
        draw,
        farm,
        fields,
        selectedFields,
        drawerOpen: false,
        ...viewState,
        rasterTiles,
        mode: "view",
      };
    }
    case MapActions.SET_MANAGE_MODE: {
      const { map, draw, farm, fields, drawerOpen } = state;
      const selectedFields = action.value
        ? [action.value]
        : state.selectedFields;

      return {
        map,
        draw,
        farm,
        fields,
        selectedFields,
        drawerOpen,
        ...manageState,
        mode: "manage",
      };
    }
    case MapActions.SET_ZONING_MODE: {
      const {
        map,
        farm,
        fields,
        selectedFields,
        selectedDate,
        selectedLayer,
        selectedAlgorithm,
        catalog,
        rasterTiles,
        algorithms,
      } = state;
      const selectedAlgorithmValue = ["COLOUR", "NDVI-R"].includes(
        selectedAlgorithm
      )
        ? "NDVI"
        : selectedAlgorithm;

      const selectedLayerValue = ["high_res", "zoning"].includes(selectedLayer)
        ? "standard"
        : selectedLayer;

      const catalogValue = ["high_res", "zoning"].includes(selectedLayer)
        ? []
        : catalog;

      const selectedDateValue = ["high_res", "zoning"].includes(selectedLayer)
        ? null
        : selectedDate;

      return {
        map,
        farm,
        fields,
        selectedFields,
        algorithms,
        selectedDate: selectedDateValue,
        selectedLayer: selectedLayerValue,
        selectedAlgorithm: selectedAlgorithm || selectedAlgorithmValue,
        catalog: catalogValue,
        rasterTiles,
        fieldSelection: !selectedFields.length,
        ...zoningState,
        mode: "zoning",
      };
    }
    case MapActions.SET_ZONING_EDIT_MODE: {
      const { map, farm, fields, selectedFields, selectedLayer } = state;
      return {
        map,
        farm,
        fields,
        selectedFields,
        mode: "edit-zone",
        zoneData: action.value,
        selectedLayer,
      };
    }
    case MapActions.SET_TUTORIAL_MODE:
      return { ...state, tutorialMode: action.value };
    case MapActions.SET_SELECTED_FIELDS: {
      if (state.inspect) {
        return { ...state };
      }

      if (state.mode === "zoning") {
        const exceedingField = state.fields.find(
          (item) =>
            item.id === action.value.fieldId && item.properties.area >= 800
        );
        if (exceedingField) {
          return { ...state };
        }
      }

      if (
        (state.mode === "zoning" || state.mode === "edit-zone") &&
        !state.fieldSelection
      ) {
        return { ...state };
      }

      let updatedSelectedFields;
      const { multiSelectEnabled, fieldId } = action.value;

      if (multiSelectEnabled && state.mode !== "manage") {
        updatedSelectedFields = state.selectedFields.includes(fieldId)
          ? state.selectedFields.filter((item) => item !== fieldId)
          : [...state.selectedFields, fieldId];
      } else {
        updatedSelectedFields = state.selectedFields.includes(fieldId)
          ? []
          : [fieldId];
      }

      const updatedStates = { selectedFields: updatedSelectedFields };

      if (state.mode === "view") {
        updatedStates.selectedDate = null;
        updatedStates.algorithms = [];
        updatedStates.selectedAlgorithm = state.selectedAlgorithm;
      }

      return { ...state, ...updatedStates };
    }
    case MapActions.REMOVE_SELECTED_FIELDS:
      return { ...state, selectedFields: [] };
    case MapActions.SET_SELECTED_LAYER: {
      const defaultAlgorithm = {
        standard: "NDVI",
        high_res: "NDVI",
        sensor_em: "SHALLOW",
        zoning: null,
      };

      return {
        ...state,
        selectedLayer: action.value,
        catalog: [],
        selectedAlgorithm: defaultAlgorithm[action.value],
        selectedDate: null,
      };
    }
    case MapActions.SET_CATALOG: {
      const selectedDate =
        action.value.length &&
        action.value.find((item) => item.date === state.selectedDate)
          ? state.selectedDate
          : null;

      return { ...state, selectedDate, catalog: action.value };
    }
    case MapActions.FETCH_MORE_CATALOG:
      return { ...state, catalog: [...state.catalog, ...action.value] };
    case MapActions.SET_SELECTED_DATE:
      return { ...state, selectedDate: action.value };
    case MapActions.SET_RASTER_LOADING:
      return { ...state, rasterLoading: action.value };
    case MapActions.SET_RASTER_TILES:
      return { ...state, rasterTiles: action.value };
    case MapActions.SET_ALGORITHMS: {
      const { algorithms, selectedAlgorithm } = action.value;

      return { ...state, algorithms, selectedAlgorithm };
    }
    case MapActions.SET_SELECTED_ALGORITHM:
      return { ...state, selectedAlgorithm: action.value };
    case MapActions.SET_NAVIGATE_FARM:
      return {
        ...state,
        selectedFields: [],
        manageStatus: null,
        currentField: null,
        drawerOpen: false,
      };
    case MapActions.SET_CREATE_BOUNDARY:
      return {
        ...state,
        manageStatus: "create-boundaries",
        selectedFields: [],
      };
    case MapActions.SET_CURRENT_FIELD:
      return { ...state, currentField: action.value };
    case MapActions.SET_CREATE_NEW_FIELD:
      return { ...state, drawerOpen: true, manageStatus: "create" };
    case MapActions.REMOVE_FIELD: {
      const fields = state.fields.filter((item) => item.id !== action.value);

      return {
        ...state,
        fields,
        selectedFields: [],
        drawerOpen: false,
        manageStatus: null,
      };
    }
    case MapActions.SET_MANAGE_STATUS:
      return { ...state, manageStatus: action.value };
    case MapActions.SET_CROP_TYPES:
      return { ...state, cropTypes: action.value };
    case MapActions.CANCEL_CREATE_FIELD:
      return {
        ...state,
        manageStatus: null,
        drawerOpen: false,
        currentField: null,
      };
    case MapActions.SAVE_NEW_FIELD: {
      if (action.value.switch) {
        return {
          ...state,
          fields: [...state.fields, action.value.field],
          currentField: null,
          manageStatus: "edit",
        };
      }

      return {
        ...state,
        drawerOpen: false,
        manageStatus: null,
        currentField: null,
        fields: [...state.fields, action.value.field],
      };
    }
    case MapActions.EDIT_SELECTED_FIELD:
      return { ...state, manageStatus: "edit", drawerOpen: true };
    case MapActions.SAVE_EDIT_FIELD: {
      const fields = state.fields.filter(
        (item) => item.id !== action.value.field.id
      );

      if (action.value.switch) {
        return { ...state, fields: [...fields, action.value.field] };
      }

      return {
        ...state,
        drawerOpen: false,
        manageStatus: null,
        selectedFields: [],
        fields: [...fields, action.value.field],
      };
    }
    case MapActions.CANCEL_EDIT_FIELD:
      return {
        ...state,
        manageStatus: null,
        drawerOpen: false,
        selectedFields: [],
      };
    case MapActions.SWITCH_FIELD: {
      const selectedFields = action.value ? [action.value] : [];

      return { ...state, selectedFields };
    }
    case MapActions.SET_MEASURE_MODE: {
      return { ...state, measureMode: action.value };
    }
    case MapActions.SET_EXPORT_MODE: {
      return { ...state, exportMode: action.value };
    }
    case MapActions.SET_BOOK_RAPID_EM: {
      const { map, draw, farm, fields, selectedFields } = state;
      return {
        map,
        draw,
        farm,
        fields,
        selectedFields,
        drawerOpen: false,
        ...rapidEmState,
        mode: "book-em",
      };
    }
    case MapActions.SET_START_ZONING: {
      return { ...state, fieldSelection: false };
    }
    case MapActions.SET_ZONE_LAYER_TRANSPARENCY: {
      return { ...state, zoneLayerTransparency: action.value };
    }
    case MapActions.SET_ZONE_LAYER_FETCHING: {
      return { ...state, zoneLayerFetching: action.value };
    }
    case MapActions.SET_ZONE_DATA: {
      return { ...state, zonedData: action.value };
    }
    case MapActions.SET_SELECTED_ZONE: {
      return { ...state, selectedZone: action.value };
    }
    case MapActions.SET_TOGGLE_CALCULATIONS: {
      return { ...state, isToggled: action.value };
    }
    case MapActions.SET_INSPECT: {
      return { ...state, inspect: action.value };
    }
    case MapActions.SET_HARVEST_MOISTURE_DATA: {
      return { ...state, harvestMoistureData: action.value };
    }
    default:
      return state;
  }
};

const MapContext = createContext();

const MapProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = { state, dispatch };

  return <MapContext.Provider value={value}>{children}</MapContext.Provider>;
};

MapProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { MapActions, MapContext, MapProvider };
